import type { ContinuousStop, GradientStop } from 'api-validations/stitch'

/**
 * Ramp for one analyte's values across one field, low to high.
 *
 * ColorBrewer's 5-class RdYlGn, which is the convention growers already read on
 * agronomic maps and stays legible for the common forms of color blindness.
 *
 * The ramp is positional, not agronomic. Sufficiency depends on crop, soil type
 * and region, and nothing in Panorama interprets a soil value, so red marks the
 * lowest reading in this field rather than a deficiency.
 *
 * Shaped as gradient stops so the same ramp drives the legend bands, the slider
 * track, and the points, rather than three lists that could drift apart.
 */
export const SOIL_GRADIENT: GradientStop[] = [
  { color: '#D7191C', stop: 0, type: 'continuous' },
  { color: '#FDAE61', stop: 0.25, type: 'continuous' },
  { color: '#FFFFBF', stop: 0.5, type: 'continuous' },
  { color: '#A6D96A', stop: 0.75, type: 'continuous' },
  { color: '#1A9641', stop: 1, type: 'continuous' }
]

/**
 * Bands in a soil legend. Past this many, the colors stop being distinguishable
 * at the size a sample point is drawn.
 */
export const SOIL_BAND_COUNT = 7

export type SoilValueRange = { min: number; max: number }

/** A sample's analyte readings, keyed by the dataset's descriptor keys. */
type SampleValues = { properties: { values: Record<string, number | undefined> } }

/**
 * The spread of a set of readings, or `null` when there are none.
 *
 * Relative to the field rather than to an absolute scale: a field whose
 * phosphorus runs 6 to 72 ppm and one that runs 20 to 24 both use the whole
 * ramp, because the question the map answers is where within this field to
 * treat differently.
 */
export const soilValueRange = (values: readonly number[]): SoilValueRange | null => {
  const [first, ...rest] = values
  if (first === undefined) return null

  return rest.reduce<SoilValueRange>(
    (range, value) => ({ min: Math.min(range.min, value), max: Math.max(range.max, value) }),
    { min: first, max: first }
  )
}

/**
 * The band a reading falls in, by the same stops the legend draws, so that a
 * point's color is one a user can find on the legend rather than a shade
 * between two of them.
 *
 * Readings outside the bounds take the end band: a user who narrows the range
 * is choosing where to spend the ramp's contrast, not hiding the samples beyond
 * it.
 */
export const soilBandColor = ({
  value,
  bands
}: {
  value: number
  bands: readonly ContinuousStop[]
}): string | undefined => {
  const band = bands.find(({ max }) => max === undefined || value <= max)
  return (band ?? bands.at(-1))?.color
}

/**
 * One analyte's readings across the samples that reported it, each with the
 * color of its band.
 *
 * Generic over the sample so the caller keeps its own geometry type: this maps
 * values to colors and does not touch where they were taken.
 */
export const coloredSoilSamples = <TSample extends SampleValues>({
  samples,
  measurement,
  bands
}: {
  samples: readonly TSample[]
  measurement: string
  bands: readonly ContinuousStop[]
}): { sample: TSample; color: string }[] =>
  samples.reduce<{ sample: TSample; color: string }[]>((colored, sample) => {
    const value = sample.properties.values[measurement]
    if (value === undefined) return colored

    const color = soilBandColor({ value, bands })
    return color === undefined ? colored : [...colored, { sample, color }]
  }, [])

/** The readings one analyte has across a field's samples, skipping samples that omit it. */
export const soilValuesFor = ({
  samples,
  measurement
}: {
  samples: readonly SampleValues[]
  measurement: string
}): number[] =>
  samples.reduce<number[]>((values, sample) => {
    const value = sample.properties.values[measurement]
    return value === undefined ? values : [...values, value]
  }, [])
